/**
 * TOOL NAVIGATION — "Your tools" + a switcher for the other two.
 *
 * Additive: it never replaces a tool's existing header controls.
 *
 * Two variants, because the toolkit genuinely has two visual languages and a
 * single style would look wrong in one of them:
 *   `ink`   — the Roadmap and Story Bible (ink/parchment/gold)
 *   `slate` — Marketing Compass (slate/teal, matching the hub)
 *
 * A flagged-off tool renders DISABLED with a "Soon" marker and does not
 * navigate. Hiding it would leave members guessing; sending them to a
 * "coming soon" page is a worse answer than a greyed chip that says so.
 */
import React from 'react';
import { switcherFor, HUB_HREF } from './toolkitNav';
import { useFeatureFlags } from '../config/featureFlags';

const STYLES = {
  ink: {
    wrap: 'flex flex-wrap items-center gap-1.5',
    hub: 'flex shrink-0 items-center gap-1.5 rounded-full border border-gold/40 bg-gold/10 px-2 py-1 text-gold transition-colors hover:bg-gold/20 sm:px-3',
    hubText: 'hidden text-xs font-semibold uppercase tracking-widest sm:inline',
    tool: 'flex shrink-0 items-center rounded-full border border-white/10 bg-white/5 px-2 py-1 text-xs font-semibold uppercase tracking-widest text-parchment/60 transition-colors hover:border-white/20 hover:text-parchment sm:px-3',
    toolOff: 'flex shrink-0 cursor-not-allowed items-center gap-1 rounded-full border border-white/5 bg-white/[0.02] px-2 py-1 text-xs font-semibold uppercase tracking-widest text-parchment/25 sm:px-3',
    soon: 'text-[9px] normal-case tracking-normal',
  },
  slate: {
    wrap: 'flex flex-wrap items-center gap-2',
    hub: 'inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-2.5 py-1.5 text-xs font-medium text-teal-800 transition-colors hover:bg-teal-100',
    hubText: '',
    tool: 'inline-flex items-center rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50',
    toolOff: 'inline-flex cursor-not-allowed items-center gap-1 rounded-lg border border-slate-100 bg-slate-50 px-2.5 py-1.5 text-xs font-medium text-slate-300',
    soon: 'text-[10px]',
  },
};

export default function ToolNav({ current, variant = 'slate', className = '' }) {
  const { flags } = useFeatureFlags();
  const s = STYLES[variant] || STYLES.slate;
  const others = switcherFor(current, flags);

  return (
    <div className={`${s.wrap} ${className}`}>
      <a href={HUB_HREF} className={s.hub} title="Back to your tools">
        <span aria-hidden>←</span>
        <span className={s.hubText}>Your tools</span>
      </a>

      {others.map((t) => {
        // Full name from `sm` up, short name on phones. aria-label carries the
        // full name either way so the accessible name never shrinks.
        const label = (
          <>
            <span className="sm:hidden">{t.short}</span>
            <span className="hidden sm:inline">{t.name}</span>
          </>
        );
        return t.available ? (
          <a key={t.id} href={t.href} className={s.tool} aria-label={t.name}>{label}</a>
        ) : (
          <span
            key={t.id}
            className={s.toolOff}
            aria-disabled="true"
            aria-label={`${t.name} — coming soon`}
            title={`${t.name} — coming soon`}
          >
            {label} <span className={s.soon}>Soon</span>
          </span>
        );
      })}
    </div>
  );
}
